'use client';

import { OpenFeature } from '@openfeature/web-sdk';
import type { Provider } from '@openfeature/web-sdk';
import { createContext, useContext, useEffect, useRef, useState } from 'react';

import { CustomFliptProvider } from './custom-flipt-provider';
import { StaticLevelTwoProvider, getE2ELevelTwoSnapshot } from './static-level-two-provider';

import type { FeatureFlagConfig } from '@/feature-flags/types';
import { logError, logInfo } from '@/feature-flags/utils/logger';

interface FeatureFlagContextValue {
  isReady: boolean;
  error: Error | null;
  provider: Provider | null;
}

const FeatureFlagContext = createContext<FeatureFlagContextValue>({
  isReady: false,
  error: null,
  provider: null,
});

interface FeatureFlagProviderProps {
  children: React.ReactNode;
  config: FeatureFlagConfig;
}

export function FeatureFlagProvider({
  children,
  config,
}: FeatureFlagProviderProps) {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [provider, setProvider] = useState<Provider | null>(null);
  const initRef = useRef(false);

  useEffect(() => {
    if (initRef.current) {
      return;
    }

    initRef.current = true;

    async function initializeProvider() {
      try {
        const e2eSnapshot = getE2ELevelTwoSnapshot();

        if (e2eSnapshot) {
          const staticProvider = new StaticLevelTwoProvider(e2eSnapshot);
          await staticProvider.initialize();

          await OpenFeature.setProviderAndWait(staticProvider);
          await OpenFeature.setContext({ targetingKey: 'medprev-web-app' });

          setProvider(staticProvider);
          setClientProvider(staticProvider);
          setIsReady(true);
          logInfo('Client-side feature flag provider initialized (static e2e provider)');
          return;
        }

        if (!config.fliptUrl) {
          throw new Error('Flipt URL is required for client provider initialization');
        }

        logInfo('Initializing client-side feature flag provider', {
          fliptUrl: config.fliptUrl,
          hasApiToken: !!config.apiToken,
        });

        const options = {
          url: config.fliptUrl,
          ...(config.apiToken && {
            authentication: {
              jwtToken: config.apiToken,
            },
          }),
        };

        const fliptProvider = new CustomFliptProvider('default', options);
        await fliptProvider.initialize();

        await OpenFeature.setProviderAndWait(fliptProvider);
        await OpenFeature.setContext({ targetingKey: 'medprev-web-app' });

        setProvider(fliptProvider);
        setClientProvider(fliptProvider);
        setIsReady(true);
        logInfo('Client-side feature flag provider initialized successfully');
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        setIsReady(true);
        logError('Failed to initialize client-side feature flag provider', error, {
          fliptUrl: config.fliptUrl,
        });
      }
    }

    initializeProvider();

    return () => {
      setClientProvider(null);
      setIsReady(false);
      logInfo('Client-side feature flag provider cleaned up on unmount');
    };
  }, [config.fliptUrl, config.apiToken]);

  return (
    <FeatureFlagContext.Provider value={{ isReady, error, provider }}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

export function useFeatureFlagProvider() {
  const context = useContext(FeatureFlagContext);

  if (!context) {
    throw new Error(
      'useFeatureFlagProvider must be used within FeatureFlagProvider',
    );
  }

  return context;
}

export function getClientInstance() {
  return OpenFeature.getClient();
}

let clientProviderInstance: Provider | null = null;

export function setClientProvider(provider: Provider | null) {
  clientProviderInstance = provider;
}

export function getClientProvider(): Provider | null {
  return clientProviderInstance;
}
